import React, { useMemo } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { BsFillTrashFill } from 'react-icons/bs';
import { useSelector } from 'react-redux';

import { renderHeaderWithHover } from '@components/Table/Columns';
import { BootstrapTable } from '@components/Table/Table';
import {
  cartApi,
  dedicatedChannelsCartApi,
  sharedChannelsCartApi,
} from '@reducers/order/order';
import { planDetailsApi } from '@reducers/plan';

const renderCountryCell = ({ row, value }) => {
  if (row.values.backorder) {
    return (
      <>
        {value} <Badge bg="info">BACKORDER</Badge>
      </>
    );
  }
  return value;
};

const getCancelQuery = ({
  backorder: isBackorder,
  is_dedicated_channel: isDedicatedChannel,
  is_pstn_upgrade: isPstnUpgrade,
  is_shared_channel: isSharedChannel,
}) => {
  if (isBackorder) {
    return cartApi.useCancelBackordersFromPlanCartMutation;
  }
  if (isDedicatedChannel) {
    return dedicatedChannelsCartApi.useCancelPlanChannelsFromCartMutation;
  }
  if (isPstnUpgrade) {
    return cartApi.useCancelPSTNUpgradeDidFromPlanCartMutation;
  }
  if (isSharedChannel) {
    return sharedChannelsCartApi.useCancelPlanChannelsFromCartMutation;
  }
  return cartApi.useCancelDidsFromPlanCartMutation;
};

const renderActionCell = ({ row }) => {
  const useCancelMutation = getCancelQuery(row.values);
  const [trigger] = useCancelMutation();
  const { plan, area, amount } = row.original;
  const { login } = useSelector((state) => state.auth.user);
  if (amount === 'Total') {
    return '';
  }

  let isButtonDisabled;

  isButtonDisabled = !(plan?.id && plan?.type && area?.area_group_id);
  if (row.values.is_shared_channel) {
    isButtonDisabled = !(plan?.id && plan?.type);
  }

  return (
    <Button
      variant="didxl"
      onClick={() => {
        trigger({
          id: plan?.id,
          type: plan?.type,
          prefixID: area?.area_group_id,
          login,
        });
      }}
      disabled={isButtonDisabled}
    >
      <BsFillTrashFill size={17} />
    </Button>
  );
};

export const CheckoutTable = ({ data }) => {
  const query = planDetailsApi.useGetFieldDescriptionQuery();
  const fieldDescriptions = query.data || {};
  const columns = useMemo(
    () => [
      {
        id: 'id',
        Header: '',
        accessor: 'id',
      },
      {
        id: 'location',
        Header: '',
        accessor: 'location',
      },
      {
        id: 'backorder',
        Header: '',
        accessor: 'is_backorder',
      },
      {
        id: 'is_dedicated_channel',
        Header: '',
        accessor: 'is_dedicated_channel',
      },
      {
        id: 'is_shared_channel',
        Header: '',
        accessor: 'is_shared_channel',
      },
      {
        id: 'is_pstn_upgrade',
        Header: '',
        accessor: 'is_pstn_upgrade',
      },
      {
        Header: () =>
          renderHeaderWithHover('Country', 'country', fieldDescriptions),
        accessor: 'country.name',
        className: 'col-md-2',
        Cell: renderCountryCell,
      },
      {
        Header: () =>
          renderHeaderWithHover('TOS', 'type_of_service', fieldDescriptions),
        accessor: 'tos',
        className: 'col-md-2',
      },
      {
        Header: () => renderHeaderWithHover('Area', 'area', fieldDescriptions),
        accessor: 'area.prefix',
        className: 'col-md-1',
      },
      {
        Header: () => renderHeaderWithHover('Plan', 'plan', fieldDescriptions),
        accessor: 'plan.name',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Outbound Plan',
            'outbound_plan',
            fieldDescriptions
          ),
        accessor: 'outbound_plan.name',
        className: 'col-md-1',
      },
      {
        Header: () => renderHeaderWithHover('#', 'amount', fieldDescriptions),
        accessor: 'amount',
        className: 'col-md-1',
      },
      {
        Header: () => renderHeaderWithHover('NRC', 'nrc', fieldDescriptions),
        accessor: 'nrc_price',
        className: 'col-md-1',
      },
      {
        Header: () => renderHeaderWithHover('MRC', 'mrc', fieldDescriptions),
        accessor: 'mrc_price',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Minimum Duration',
            'duration',
            fieldDescriptions
          ),
        accessor: 'duration',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover('Action', 'action', fieldDescriptions),
        accessor: 'action',
        className: 'col-md-1',
        Cell: renderActionCell,
      },
    ],
    []
  );
  return (
    <BootstrapTable
      columns={columns}
      data={data}
      tableProps={() => ({
        responsive: true,
        bordered: true,
        className: 'normalFontSize',
      })}
      hiddenColumns={[
        'id',
        'location',
        'backorder',
        'is_dedicated_channel',
        'is_pstn_upgrade',
        'is_shared_channel',
      ]}
    />
  );
};
